import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import {
  getAuthSession,
  getValidAuthSession,
  saveSessionFromUrl,
  updateUserMetadata,
} from "@/lib/supabaseAuth";

const normalizeUserType = (type, mode) => {
  if (type === "dueno") return "propietario";
  if (type === "corredor") return "agente";
  if (type) return type;
  return mode === "publisher" ? "propietario" : "buscador";
};

const CompletarPerfil = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "general";
  const next = searchParams.get("next") || "/";
  const allowSearchUser = mode !== "publisher";
  const [session, setSession] = useState(() => getAuthSession());
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    tipoUsuario: normalizeUserType("", mode),
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userMetadata = useMemo(
    () => session?.user?.user_metadata || {},
    [session]
  );

  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      const urlSession = await saveSessionFromUrl(true);
      const currentSession = urlSession || (await getValidAuthSession());

      if (!isMounted) return;
      if (!currentSession?.access_token) {
        navigate("/iniciar-sesion", { replace: true });
        return;
      }

      const metadata = currentSession.user?.user_metadata || {};
      const existingType = normalizeUserType(metadata.tipo_usuario, mode);
      const existingPhone = String(metadata.telefono || "").replace(/^\+52\s*/, "");
      const existingName =
        metadata.nombre ||
        metadata.full_name ||
        metadata.name ||
        currentSession.user?.email ||
        "";

      if (metadata.telefono && metadata.tipo_usuario) {
        navigate(next, { replace: true });
        return;
      }

      setSession(currentSession);
      setForm({
        nombre: existingName,
        telefono: existingPhone,
        tipoUsuario:
          !allowSearchUser && existingType === "buscador" ? "propietario" : existingType,
      });
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, [allowSearchUser, mode, navigate]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await updateUserMetadata({
        accessToken: session.access_token,
        metadata: {
          ...userMetadata,
          nombre: form.nombre.trim(),
          telefono: `+52 ${form.telefono}`.trim(),
          tipo_usuario: form.tipoUsuario,
          tipo_anunciante: form.tipoUsuario === "buscador" ? "" : form.tipoUsuario,
          interes_buscar: form.tipoUsuario === "buscador",
          interes_publicar: form.tipoUsuario !== "buscador",
        },
      });

      navigate(next, { replace: true });
    } catch (profileError) {
      setError(profileError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        <section className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-black text-slate-950">
            Completa tu perfil
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Google nos confirma tu correo. Estos datos nos ayudan a identificar
            tu cuenta y darte la experiencia correcta dentro de Tulocalmx.
          </p>

          <form onSubmit={handleSubmit} className="mt-5 space-y-3">
            <input
              required
              type="text"
              placeholder="Nombre completo"
              value={form.nombre}
              onChange={(event) => updateForm("nombre", event.target.value)}
              className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
            />
            <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-[#0056D2]">
              <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-black text-slate-700">
                +52
              </span>
              <input
                required
                type="tel"
                placeholder="WhatsApp"
                value={form.telefono}
                onChange={(event) => updateForm("telefono", event.target.value)}
                className="w-full p-3 outline-none"
              />
            </div>
            <select
              value={form.tipoUsuario}
              onChange={(event) => updateForm("tipoUsuario", event.target.value)}
              className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
            >
              {allowSearchUser && <option value="buscador">Buscador</option>}
              <option value="propietario">Propietario</option>
              <option value="inmobiliaria">Inmobiliaria</option>
              <option value="agente">Agente</option>
            </select>

            {error && (
              <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#F97316] p-3 font-black text-white disabled:bg-orange-300"
            >
              {isSubmitting ? "Guardando..." : "Continuar"}
            </button>
          </form>
        </section>
      </main>
    </>
  );
};

export default CompletarPerfil;
